import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { BACKEND_BASE_URL } from '../constants';

interface FeedbackComponentProps {
  sessionId: string;
  messageId?: string;
  onFeedbackSubmitted?: () => void;
}

interface FeedbackFormData {
  rating: 'positive' | 'negative' | null;
  comment: string;
  category?: string;
}

export const FeedbackComponent: React.FC<FeedbackComponentProps> = ({
  sessionId,
  messageId,
  onFeedbackSubmitted,
  language
}) => {
  const { t } = useTranslation(language);
  const [showForm, setShowForm] = useState(false);
  const [feedbackData, setFeedbackData] = useState<FeedbackFormData>({
    rating: null,
    comment: '',
    category: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleThumbClick = (rating: 'positive' | 'negative') => {
    setFeedbackData({ ...feedbackData, rating });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackData.rating) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          message_id: messageId,
          rating: feedbackData.rating,
          comment: feedbackData.comment,
          category: feedbackData.category,
          timestamp: new Date().toISOString()
        }),
      });

      if (response.ok) {
        setHasSubmitted(true);
        setShowForm(false);
        onFeedbackSubmitted?.();
        
        // Reset after a delay
        setTimeout(() => {
          setHasSubmitted(false);
          setFeedbackData({ rating: null, comment: '', category: '' });
        }, 3000);
      } else {
        console.error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFeedbackData({ rating: null, comment: '', category: '' });
  };

  if (hasSubmitted) {
    return (
      <div className="feedback-component feedback-success">
        <i className="fas fa-check-circle"></i>
        <span>{t('feedback.thankYou')}</span>
      </div>
    );
  }

  return (
    <div className="feedback-component">
      <div className="feedback-buttons">
        <button
          className={`feedback-btn feedback-positive ${feedbackData.rating === 'positive' ? 'active' : ''}`}
          onClick={() => handleThumbClick('positive')}
          title={t('feedback.helpful')}
          disabled={hasSubmitted}
        >
          <i className="fas fa-thumbs-up"></i>
        </button>
        <button
          className={`feedback-btn feedback-negative ${feedbackData.rating === 'negative' ? 'active' : ''}`}
          onClick={() => handleThumbClick('negative')}
          title={t('feedback.notHelpful')}
          disabled={hasSubmitted}
        >
          <i className="fas fa-thumbs-down"></i>
        </button>
      </div>

      {showForm && (
        <div className="feedback-popup-overlay" onClick={handleCancel}>
          <div className="feedback-popup" onClick={(e) => e.stopPropagation()}>
            <h3>
              {feedbackData.rating === 'positive' 
                ? t('feedback.whatDidYouLike') 
                : t('feedback.whatCouldBeImproved')}
            </h3>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="feedback-category">{t('feedback.category')}</label>
                <select
                  id="feedback-category"
                  value={feedbackData.category}
                  onChange={(e) => setFeedbackData({ ...feedbackData, category: e.target.value })}
                >
                  <option value="">{t('feedback.selectCategory')}</option>
                  <option value="accuracy">{t('feedback.categories.accuracy')}</option>
                  <option value="helpfulness">{t('feedback.categories.helpfulness')}</option>
                  <option value="speed">{t('feedback.categories.responseSpeed')}</option>
                  <option value="clarity">{t('feedback.categories.clarity')}</option>
                  <option value="other">{t('feedback.categories.other')}</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="feedback-comment">{t('feedback.comments')}</label>
                <textarea
                  id="feedback-comment"
                  value={feedbackData.comment}
                  onChange={(e) => setFeedbackData({ ...feedbackData, comment: e.target.value })}
                  placeholder={t('feedback.placeholder')}
                  rows={4}
                />
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel" 
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  {t('feedback.cancel')}
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('feedback.submitting') : t('feedback.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};